import { useTranslation } from '@/i18n';
import { useCallback, useMemo, useRef, useState } from 'react';
import { DEFAULT_ANCHOR, getAnchorItems } from './anchorData';
import { useAnchorClick } from './useAnchorClick';
import { useScrollDetection } from './useScrollDetection';
import { useScrollToAnchor } from './useScrollToAnchor';
import { scrollDefaultToTop } from './utils';

export const useHandleAnchor = (
  contentWrapperRef: React.RefObject<HTMLDivElement>,
  isReconciliationSettingEnabled: boolean = false,
  isApAiServiceSettingsEnabled: boolean = false
) => {
  const [currentAnchor, setCurrentAnchor] = useState<string>(DEFAULT_ANCHOR);
  const isClickingRef = useRef(false);
  const isInitializingRef = useRef(true);
  // Target scroll position for manual scroll detection
  const targetScrollRef = useRef<number | null>(null);
  // Shared cache for last anchor to sync between click and scroll detection
  const lastAnchorRef = useRef<string | null>(null);

  const { t } = useTranslation();
  const anchorItems = useMemo(
    () =>
      getAnchorItems(
        t,
        isReconciliationSettingEnabled,
        isApAiServiceSettingsEnabled
      ),
    [t, isReconciliationSettingEnabled, isApAiServiceSettingsEnabled]
  );

  const { handleAnchorClick, cleanupScrollDetection, scrollToHref } =
    useAnchorClick(
      contentWrapperRef,
      isClickingRef,
      setCurrentAnchor,
      anchorItems,
      targetScrollRef,
      lastAnchorRef
    );

  const resetAnchor = useCallback(() => {
    isInitializingRef.current = true;
    isClickingRef.current = false;
    targetScrollRef.current = null;
    lastAnchorRef.current = null;
    cleanupScrollDetection();
    setCurrentAnchor(DEFAULT_ANCHOR);
    scrollDefaultToTop(contentWrapperRef);

    // Re-enable scroll detection after a short delay
    setTimeout(() => {
      isInitializingRef.current = false;
    }, 300);
  }, [cleanupScrollDetection, contentWrapperRef]);

  useScrollToAnchor(
    contentWrapperRef,
    isClickingRef,
    isInitializingRef,
    setCurrentAnchor,
    anchorItems
  );
  useScrollDetection(
    contentWrapperRef,
    isClickingRef,
    isInitializingRef,
    setCurrentAnchor,
    anchorItems,
    targetScrollRef,
    lastAnchorRef
  );

  const getContainer = useCallback(() => {
    return contentWrapperRef.current || window;
  }, [contentWrapperRef]);

  const getCurrentAnchor = useCallback(() => {
    return currentAnchor;
  }, [currentAnchor]);

  /**
   * Programmatically scroll to a section by its anchor href.
   * This is useful when entering edit mode or navigating programmatically.
   */
  const scrollToSection = useCallback(
    (href: string) => {
      scrollToHref(href);
    },
    [scrollToHref]
  );

  return {
    currentAnchor,
    handleAnchorClick,
    anchorItems,
    getContainer,
    getCurrentAnchor,
    resetAnchor,
    scrollToSection,
  };
};
